/**
 * Pure parts of the firma's rental settings form (P5-b2): the text the boxes hold, what changed against the stored values, and the
 * legacy starting values. The ranges are the server's — it answers with its own message — so this only turns text into numbers and
 * names what it cannot read. The save is SLICE-ONLY: a field travels only when it differs from what was loaded.
 */

import type { PujcovnaNastaveni } from './api';
import { cislo } from './cenik-helpers';
import { parseMezera } from './helpers';

export interface NastaveniValue { mezera: string; s2: string; s3: string; s4: string; limit: string; dni: string; en: string; et: string; ek: string }

const text = (n: number | null | undefined): string => (n == null ? '' : String(n));

export function nastaveniSeed(n: Partial<PujcovnaNastaveni>): NastaveniValue {
  return { mezera: text(n.mezera_dni ?? 0), s2: text(n.skupina2_proc), s3: text(n.skupina3_proc), s4: text(n.skupina4_proc), limit: text(n.loajalita_limit), dni: text(n.loajalita_dni), en: n.email_nazev ?? '', et: n.email_telefon ?? '', ek: n.email_kontakt ?? '' };
}

/** The values of the original system: groups at 85 / 80 / 75 % of the maximum, loyal by 20 000 Kč paid within 365 days. */
export const NASTAVENI_VYCHOZI: Pick<NastaveniValue, 's2' | 's3' | 's4' | 'limit' | 'dni'> = { s2: '85', s3: '80', s4: '75', limit: '20000', dni: '365' };

export type NastaveniZmena = { ok: true; body: Partial<PujcovnaNastaveni> } | { ok: false; chyba: string };

const POLE = [
  ['s2', 'skupina2_proc', 'Cena skupiny 2'],
  ['s3', 'skupina3_proc', 'Cena skupiny 3'],
  ['s4', 'skupina4_proc', 'Cena skupiny 4'],
  ['limit', 'loajalita_limit', 'Limit útraty'],
  ['dni', 'loajalita_dni', 'Období věrnosti'],
] as const;

/** The texts of the rental e-mails' identity: sent trimmed, a blank is "not set" (null). */
const TEXTY = [
  ['en', 'email_nazev'],
  ['et', 'email_telefon'],
  ['ek', 'email_kontakt'],
] as const;

/** What differs from `stored`, as a request body; an empty body = nothing to save. A box that is not a number stops the save, named. */
export function nastaveniZmena(stored: PujcovnaNastaveni, v: NastaveniValue): NastaveniZmena {
  const body: Partial<PujcovnaNastaveni> = {};
  const mezera = parseMezera(v.mezera);
  if (mezera == null) return { ok: false, chyba: 'Mezera musí být celé číslo dní.' };
  if (mezera !== stored.mezera_dni) body.mezera_dni = mezera;
  for (const [klic, pole, popisek] of POLE) {
    const n = cislo(v[klic]);
    if (n !== null && Number.isNaN(n)) return { ok: false, chyba: `${popisek}: „${v[klic]}“ není číslo.` };
    if (n !== (stored[pole] ?? null)) body[pole] = n as never;
  }
  for (const [klic, pole] of TEXTY) {
    const t = v[klic].trim();
    if (t !== (stored[pole] ?? '')) body[pole] = (t || null) as never;
  }
  return { ok: true, body };
}

/** Two box texts mean the same value (`82,5` = `82.5`, `085` = `85`); non-numbers compare as trimmed text. */
/** The free-text boxes (the e-mail identity): compared as trimmed text, never as numbers (a phone number is not a quantity). */
const TEXT_KLICE: ReadonlyArray<keyof NastaveniValue> = ['en', 'et', 'ek'];

const stejne = (a: string, b: string, klic?: keyof NastaveniValue): boolean => {
  if (klic && TEXT_KLICE.includes(klic)) return a.trim() === b.trim();
  const x = cislo(a);
  const y = cislo(b);
  return x !== null && y !== null && !Number.isNaN(x) && !Number.isNaN(y) ? x === y : a.trim() === b.trim();
};

/** The boxes plus the stored values they were last aligned to (`base`): what tells an untouched box from one the user is editing. */
export interface NastaveniForm { v: NastaveniValue; base: NastaveniValue }

/**
 * The stored settings changed under an open form (another admin saved, or this form's own save came back). A box the user has NOT
 * touched takes the new stored value; a box they are editing keeps their text (its `base` stays behind, which `nastaveniKonflikty`
 * reports so the form can tell them the value moved). A box that already shows the new value is simply aligned. Returns `f` itself when
 * nothing changes.
 */
export function nastaveniPrenes(f: NastaveniForm, novy: NastaveniValue): NastaveniForm {
  let v = f.v;
  let base = f.base;
  for (const k of Object.keys(novy) as (keyof NastaveniValue)[]) {
    if (base[k] === novy[k]) continue;
    if (stejne(v[k], base[k], k) || stejne(v[k], novy[k], k)) {
      v = v[k] === novy[k] ? v : { ...v, [k]: novy[k] }; // an equal value typed another way (82,5) is normalised to what is stored
      base = { ...base, [k]: novy[k] };
    }
  }
  return v === f.v && base === f.base ? f : { v, base };
}

/** The boxes being edited whose stored value moved meanwhile, with the value now stored. */
export function nastaveniKonflikty(stored: NastaveniValue, f: NastaveniForm): Partial<Record<keyof NastaveniValue, string>> {
  const out: Partial<Record<keyof NastaveniValue, string>> = {};
  for (const k of Object.keys(stored) as (keyof NastaveniValue)[]) if (f.base[k] !== stored[k] && !stejne(f.v[k], stored[k], k)) out[k] = stored[k];
  return out;
}
